import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Inbox, ArrowLeft, Lock, LogOut, BellRing, CheckCheck, Download, CalendarClock, Image as ImageIcon, X, FileText, Users, Mail, Phone, Send, MessageCircle, Trash2, StickyNote } from "lucide-react";
import { generateLeadReportPdf } from "../lib/leadReportPdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fe_admin_token";

function PasscodeGate({ onAuth }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Incorrect passcode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone text-ink flex items-center justify-center px-6" data-testid="admin-gate">
      <form onSubmit={submit} className="w-full max-w-md bg-sand rounded-3xl border border-stone/20 p-10">
        <div className="font-serif italic text-3xl">Fresh Earth</div>
        <div className="flex items-center gap-2 mt-6 text-moss">
          <Lock size={18} /> <span className="text-xs tracking-[0.16em] uppercase font-semibold">Team access only</span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight mt-3">Enter passcode</h1>
        <p className="text-slate text-sm mt-2">Pilot requests are protected. Enter the team passcode to continue.</p>
        <input
          data-testid="admin-passcode"
          type="password"
          autoFocus
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          placeholder="Passcode"
          className="w-full bg-transparent rounded-none border-0 border-b border-stone/30 py-3 mt-8 text-lg text-ink placeholder:text-stone/60 focus:outline-none focus:border-moss transition-colors duration-300"
        />
        <button
          type="submit"
          data-testid="admin-passcode-submit"
          disabled={loading}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-forest text-bone text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-moss transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : null}
          {loading ? "Checking…" : "Unlock"}
        </button>
        <Link to="/" className="block mt-6 text-sm text-slate hover:text-ink transition-colors duration-300">← Back to site</Link>
      </form>
    </div>
  );
}

/** Auth-safe image: fetches as blob using bearer token, so <img> can render it. */
function AuthImage({ path, token, className, alt, onClick, testId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    (async () => {
      try {
        const res = await axios.get(`${API}/files/${path}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        objectUrl = URL.createObjectURL(res.data);
        if (!cancelled) setSrc(objectUrl);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, token]);
  if (err) {
    return <div className={`${className || ""} bg-stone/10 flex items-center justify-center text-stone`}><ImageIcon size={16} /></div>;
  }
  if (!src) {
    return <div className={`${className || ""} bg-stone/10 flex items-center justify-center`}><Loader2 size={14} className="animate-spin text-stone" /></div>;
  }
  return <img src={src} alt={alt || ""} className={className} onClick={onClick} data-testid={testId} />;
}

function Lightbox({ path, token, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-ink/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="photo-lightbox">
      <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-bone/10 hover:bg-bone/20 text-bone flex items-center justify-center" aria-label="Close">
        <X size={18} />
      </button>
      <AuthImage
        path={path}
        token={token}
        className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl"
        alt="Site photo"
      />
    </div>
  );
}

function OfficerNotes({ officer, onAdd, onDelete }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = officer.notes || [];
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await onAdd(body);
      setBody("");
    } finally {
      setBusy(false);
    }
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  return (
    <div className="mt-6 border-t border-stone/15 pt-5" data-testid={`officer-notes-${officer.email}`}>
      <div className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-moss font-semibold">
        <StickyNote size={13} /> Private team notes
      </div>
      {notes.length > 0 && (
        <ul className="mt-3 space-y-2">
          {notes.map((n) => (
            <li
              key={n.id}
              data-testid={`officer-note-${n.id}`}
              className="flex items-start justify-between gap-3 rounded-xl bg-bone border border-stone/20 p-3"
            >
              <div className="flex-1">
                <div className="text-sm text-ink whitespace-pre-wrap">{n.body}</div>
                <div className="mt-1 text-[10px] tracking-wide uppercase text-stone">{fmt(n.created_at)}{n.author ? ` · ${n.author}` : ""}</div>
              </div>
              <button
                type="button"
                onClick={() => onDelete(n.id)}
                data-testid={`officer-note-delete-${n.id}`}
                className="text-stone hover:text-terracotta transition-colors"
                aria-label="Delete note"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="mt-3 flex flex-col md:flex-row gap-3">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Add a note the team will see next time this officer reaches out…"
          data-testid={`officer-note-input-${officer.email}`}
          className="flex-1 bg-bone border border-stone/25 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-moss transition-colors resize-none"
        />
        <button
          type="submit"
          disabled={busy || !body.trim()}
          data-testid={`officer-note-save-${officer.email}`}
          className="self-end md:self-stretch inline-flex items-center justify-center gap-2 rounded-full bg-forest text-bone px-5 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-moss disabled:opacity-50 transition-colors whitespace-nowrap"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <StickyNote size={13} />} Save note
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("leads"); // "leads" | "officers"
  const [leads, setLeads] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [expanded, setExpanded] = useState({}); // officer email -> expanded
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [lightboxPath, setLightboxPath] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null); // lead id currently generating
  const [reminderBusy, setReminderBusy] = useState(null); // lead id

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [leadsRes, unreadRes, officersRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads/unread_count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/officers`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLeads(leadsRes.data);
      setUnread(unreadRes.data.unread);
      setOfficers(officersRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/leads/mark_read`, {}, authHeader);
      await load();
      toast.success("All requests marked as read");
    } catch (e) {
      toast.error("Could not update");
    } finally {
      setMarking(false);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const fmtSlot = (hhmm) => {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
  };

  const exportCsv = () => {
    const headers = ["Name", "Email", "Phone", "Organization", "Segment", "Message", "Language", "Callback Date", "Callback Slot (IST)", "Reminder Sent", "Photos", "Read", "Received"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) => [
      l.name, l.email, l.phone, l.organization, l.segment, l.message,
      l.lang || "en",
      l.callback_date || "", l.callback_slot || "",
      l.reminder_sent ? "yes" : "",
      (l.photos || []).length,
      l.read ? "read" : "new",
      l.created_at,
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fresh-earth-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadReport = async (lead) => {
    setPdfBusy(lead.id);
    try {
      await generateLeadReportPdf(lead, token);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(null);
    }
  };

  const sendReminder = async (leadId) => {
    setReminderBusy(leadId);
    try {
      await axios.post(`${API}/leads/${leadId}/send-reminder`, {}, authHeader);
      toast.success("Reminder sent");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send reminder");
    } finally {
      setReminderBusy(null);
    }
  };

  const startConversation = (officer) => {
    const subject = encodeURIComponent(`Re: Fresh Earth · OED pilot for ${officer.organization || officer.name}`);
    const body = encodeURIComponent(`Hi ${officer.name.split(" ")[0]},\n\nFollowing up on your enquiry with Fresh Earth. Would you have 15 minutes this week to discuss the next steps for an OED pilot at your site?\n\nBest,\nFresh Earth team\ncare@fresh-earth.com\n+91 98995 34141`);
    window.location.href = `mailto:${officer.email}?subject=${subject}&body=${body}`;
  };

  const openWhatsApp = (phone, name, lang, lead) => {
    const digits = String(phone || "").replace(/\D/g, "");
    if (!digits) { toast.error("No phone number on file"); return; }
    // If phone doesn't start with a country code, assume India (+91)
    const withCc = digits.length === 10 ? `91${digits}` : digits;
    const first = (name || "").split(" ")[0] || "";
    let msg;
    if (lang === "hi") {
      msg = lead && lead.callback_date
        ? `नमस्ते ${first}, यह Fresh Earth OED टीम से एक अनुस्मारक है। हमारी कॉल ${lead.callback_date} · ${fmtSlot(lead.callback_slot || "")} पर निर्धारित है। किसी भी बदलाव के लिए कृपया हमें बताएं। — care@fresh-earth.com`
        : `नमस्ते ${first}, Fresh Earth OED टीम की ओर से नमस्कार। क्या हम आपकी साइट के लिए अगले चरण पर 15 मिनट की कॉल कर सकते हैं?`;
    } else {
      msg = lead && lead.callback_date
        ? `Hi ${first}, this is a reminder from Fresh Earth (OED). Our call is scheduled for ${lead.callback_date} · ${fmtSlot(lead.callback_slot || "")}. Reply here if anything changes. — care@fresh-earth.com`
        : `Hi ${first}, this is Fresh Earth (OED). Could we schedule 15 minutes to discuss the next steps for your site?`;
    }
    const url = `https://wa.me/${withCc}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  const addNote = async (email, body) => {
    const trimmed = (body || "").trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API}/officers/${encodeURIComponent(email)}/notes`, { body: trimmed }, authHeader);
      toast.success("Note saved");
      await load();
    } catch (e) {
      toast.error("Could not save note");
    }
  };

  const deleteNote = async (email, noteId) => {
    try {
      await axios.delete(`${API}/officers/${encodeURIComponent(email)}/notes/${noteId}`, authHeader);
      await load();
    } catch (e) {
      toast.error("Could not delete note");
    }
  };

  if (!token) return <PasscodeGate onAuth={setToken} />;

  return (
    <div className="min-h-screen bg-bone text-ink" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <div className="font-serif italic text-3xl">Fresh Earth</div>
            <div className="flex items-center gap-3 mt-3">
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">{tab === "leads" ? "Pilot Requests" : "Officer Address Book"}</h1>
              {tab === "leads" && unread > 0 && (
                <span data-testid="unread-badge" className="inline-flex items-center gap-1.5 bg-terracotta text-bone text-xs tracking-[0.1em] uppercase px-3 py-1.5 rounded-full">
                  <BellRing size={13} /> {unread} new
                </span>
              )}
            </div>
            <p className="text-slate mt-2">
              {loading
                ? "Loading…"
                : tab === "leads"
                  ? `${leads.length} submission${leads.length === 1 ? "" : "s"}`
                  : `${officers.length} unique officer${officers.length === 1 ? "" : "s"}`}
            </p>
            <div className="mt-5 inline-flex rounded-full border border-stone/25 p-1 bg-sand" data-testid="admin-tabs">
              <button
                onClick={() => setTab("leads")}
                data-testid="tab-leads"
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs tracking-[0.14em] uppercase transition-colors duration-200 ${tab === "leads" ? "bg-forest text-bone" : "text-slate hover:text-ink"}`}
              >
                <Inbox size={14} /> Leads
              </button>
              <button
                onClick={() => setTab("officers")}
                data-testid="tab-officers"
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs tracking-[0.14em] uppercase transition-colors duration-200 ${tab === "officers" ? "bg-forest text-bone" : "text-slate hover:text-ink"}`}
              >
                <Users size={14} /> Officers
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {tab === "leads" && leads.length > 0 && (
              <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-full border border-stone/30 px-5 py-3 text-sm hover:bg-sand transition-colors duration-300">
                <Download size={15} /> Export CSV
              </button>
            )}
            {tab === "leads" && unread > 0 && (
              <button onClick={markAllRead} data-testid="mark-read-btn" disabled={marking} className="inline-flex items-center gap-2 rounded-full bg-forest text-bone px-5 py-3 text-sm hover:bg-moss transition-colors duration-300 disabled:opacity-60">
                {marking ? <Loader2 size={15} className="animate-spin" /> : <CheckCheck size={15} />} Mark all read
              </button>
            )}
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-stone/30 px-5 py-3 text-sm hover:bg-sand transition-colors duration-300">
              <LogOut size={15} /> Sign out
            </button>
            <Link to="/" data-testid="admin-back" className="inline-flex items-center gap-2 rounded-full border border-stone/30 px-5 py-3 text-sm hover:bg-sand transition-colors duration-300">
              <ArrowLeft size={16} /> Site
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate py-20"><Loader2 className="animate-spin" size={20} /> Loading…</div>
        ) : tab === "leads" ? (
          leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-28 text-center text-slate">
            <Inbox size={40} strokeWidth={1.2} className="text-stone mb-4" />
            <p className="font-serif text-2xl text-ink">No pilot requests yet</p>
            <p className="mt-2">New submissions from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-stone/20 bg-sand" data-testid="leads-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-stone/20 text-xs tracking-[0.12em] uppercase text-stone">
                  <th className="px-5 py-4"></th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Organization</th>
                  <th className="px-5 py-4">Segment</th>
                  <th className="px-5 py-4">Callback</th>
                  <th className="px-5 py-4">Site Photos</th>
                  <th className="px-5 py-4">Message</th>
                  <th className="px-5 py-4">Received</th>
                  <th className="px-5 py-4">Report</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className={`border-b border-stone/10 align-top transition-colors duration-200 ${l.read ? "hover:bg-bone/60" : "bg-terracotta/[0.06] hover:bg-terracotta/10"}`}>
                    <td className="px-5 py-4">{!l.read && <span title="New" className="inline-block w-2.5 h-2.5 rounded-full bg-terracotta" />}</td>
                    <td className="px-5 py-4 font-medium text-ink">{l.name}</td>
                    <td className="px-5 py-4 text-moss">{l.email}</td>
                    <td className="px-5 py-4 text-slate">{l.phone || "—"}</td>
                    <td className="px-5 py-4 text-slate">{l.organization || "—"}</td>
                    <td className="px-5 py-4 text-slate">{l.segment || "—"}</td>
                    <td className="px-5 py-4 text-slate whitespace-nowrap" data-testid={`lead-callback-${l.id}`}>
                      {l.callback_date ? (
                        <div className="flex flex-col gap-1.5">
                          <span className="inline-flex items-center gap-1.5 text-ink">
                            <CalendarClock size={13} className="text-moss" />
                            {l.callback_date}
                            {l.callback_slot && <span className="text-stone">· {fmtSlot(l.callback_slot)}</span>}
                          </span>
                          <div className="flex items-center gap-2 flex-wrap">
                            {l.reminder_sent ? (
                              <span className="text-[10px] tracking-[0.12em] uppercase text-moss inline-flex items-center gap-1"><CheckCheck size={10} /> Reminded</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => sendReminder(l.id)}
                                disabled={reminderBusy === l.id}
                                data-testid={`send-reminder-${l.id}`}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase text-terracotta hover:text-forest transition-colors disabled:opacity-50"
                              >
                                {reminderBusy === l.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                                Email
                              </button>
                            )}
                            {l.phone && (
                              <button
                                type="button"
                                onClick={() => openWhatsApp(l.phone, l.name, l.lang, l)}
                                data-testid={`whatsapp-reminder-${l.id}`}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase text-[#25D366] hover:text-forest transition-colors"
                              >
                                <MessageCircle size={10} /> WhatsApp
                              </button>
                            )}
                            {l.lang === "hi" && <span className="text-[10px] tracking-[0.14em] uppercase text-stone">हिं</span>}
                          </div>
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-4">
                      {l.photos && l.photos.length > 0 ? (
                        <div className="flex gap-1.5" data-testid={`lead-photos-${l.id}`}>
                          {l.photos.slice(0, 4).map((p, idx) => (
                            <button
                              type="button"
                              key={p.storage_path}
                              onClick={() => setLightboxPath(p.storage_path)}
                              className="w-10 h-10 rounded-md overflow-hidden border border-stone/30 hover:ring-2 hover:ring-moss transition-all"
                              title={p.original_filename || "Site photo"}
                              data-testid={`lead-photo-thumb-${l.id}-${idx}`}
                            >
                              <AuthImage
                                path={p.storage_path}
                                token={token}
                                className="w-full h-full object-cover"
                                alt={p.original_filename}
                              />
                            </button>
                          ))}
                          {l.photos.length > 4 && (
                            <span className="w-10 h-10 rounded-md border border-stone/30 bg-bone flex items-center justify-center text-xs text-stone">+{l.photos.length - 4}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-stone">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate max-w-xs">{l.message || "—"}</td>
                    <td className="px-5 py-4 text-stone whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => downloadReport(l)}
                        disabled={pdfBusy === l.id}
                        data-testid={`download-report-${l.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone/30 hover:border-forest px-3 py-1.5 text-xs hover:bg-forest hover:text-bone transition-colors disabled:opacity-50"
                      >
                        {pdfBusy === l.id ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
        ) : (
          /* ---- Officers tab ---- */
          officers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-28 text-center text-slate" data-testid="officers-empty">
              <Users size={40} strokeWidth={1.2} className="text-stone mb-4" />
              <p className="font-serif text-2xl text-ink">No officers yet</p>
              <p className="mt-2">Every unique enquiry email will appear here.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-stone/20 bg-sand divide-y divide-stone/15" data-testid="officers-list">
              {officers.map((o) => {
                const isOpen = !!expanded[o.email];
                return (
                  <div key={o.email} data-testid={`officer-row-${o.email}`} className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-serif text-2xl text-ink">{o.name}</div>
                          {o.lang === "hi" && (
                            <span className="text-[10px] tracking-[0.14em] uppercase text-stone border border-stone/30 rounded-full px-2 py-0.5">हिंदी</span>
                          )}
                          {o.unread_count > 0 && (
                            <span className="inline-flex items-center gap-1.5 bg-terracotta text-bone text-[10px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full">
                              <BellRing size={11} /> {o.unread_count} new
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                          <span className="inline-flex items-center gap-1.5 text-moss"><Mail size={13} /> {o.email}</span>
                          {o.phone && <span className="inline-flex items-center gap-1.5 text-slate"><Phone size={13} /> {o.phone}</span>}
                          {o.organization && <span className="text-slate">· {o.organization}</span>}
                        </div>
                        <div className="mt-2 text-xs text-stone tracking-wide">
                          {o.enquiries_count} enquir{o.enquiries_count === 1 ? "y" : "ies"} · last seen {fmt(o.last_seen)}
                          {(o.notes || []).length > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-moss">
                              <StickyNote size={11} /> {o.notes.length} note{o.notes.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => startConversation(o)}
                          data-testid={`officer-start-${o.email}`}
                          className="inline-flex items-center gap-2 rounded-full bg-forest text-bone px-5 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-moss transition-colors"
                        >
                          <Mail size={13} /> Start a fresh conversation
                        </button>
                        {o.phone && (
                          <button
                            type="button"
                            onClick={() => openWhatsApp(o.phone, o.name, o.lang)}
                            data-testid={`officer-whatsapp-${o.email}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/60 text-[#128C7E] px-4 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-[#25D366]/10 transition-colors"
                          >
                            <MessageCircle size={13} /> WhatsApp
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpanded((s) => ({ ...s, [o.email]: !s[o.email] }))}
                          data-testid={`officer-toggle-${o.email}`}
                          className="inline-flex items-center gap-2 rounded-full border border-stone/30 px-4 py-2.5 text-xs tracking-[0.1em] uppercase text-slate hover:text-ink"
                        >
                          {isOpen ? "Hide history" : "Show history"}
                        </button>
                      </div>
                    </div>
                    {/* Private team notes */}
                    <OfficerNotes
                      officer={o}
                      onAdd={(body) => addNote(o.email, body)}
                      onDelete={(id) => deleteNote(o.email, id)}
                    />
                    {isOpen && (
                      <div className="mt-6 space-y-4" data-testid={`officer-history-${o.email}`}>
                        {(o.leads || []).map((L) => (
                          <div key={L.id} className="rounded-xl border border-stone/20 bg-bone p-5">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              <div className="text-xs tracking-[0.12em] uppercase text-stone">{fmt(L.created_at)}</div>
                              {L.segment && <div className="text-xs text-slate">{L.segment}</div>}
                            </div>
                            {L.callback_date && (
                              <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-ink">
                                <CalendarClock size={13} className="text-moss" />
                                Callback {L.callback_date} {L.callback_slot && <>· {fmtSlot(L.callback_slot)}</>}
                              </div>
                            )}
                            {L.message && (
                              <div className="mt-3 text-sm text-slate whitespace-pre-wrap">{L.message}</div>
                            )}
                            {L.photos && L.photos.length > 0 && (
                              <div className="mt-3 flex gap-1.5">
                                {L.photos.slice(0, 6).map((p, idx) => (
                                  <button
                                    key={p.storage_path}
                                    type="button"
                                    onClick={() => setLightboxPath(p.storage_path)}
                                    className="w-14 h-14 rounded-md overflow-hidden border border-stone/30 hover:ring-2 hover:ring-moss transition-all"
                                    data-testid={`officer-photo-${o.email}-${idx}`}
                                  >
                                    <AuthImage path={p.storage_path} token={token} className="w-full h-full object-cover" alt="" />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
      {lightboxPath && (
        <Lightbox path={lightboxPath} token={token} onClose={() => setLightboxPath(null)} />
      )}
    </div>
  );
}
